/**
 * Mock data for User Management
 */

import type {
  User,
  UserStatus,
  UserType,
  UserDetail,
  UserTask,
  UserPayment,
  UserWarning,
  UserActivity,
  PaymentMethod,
  SpyDollarBalance,
  SpyDollarTransaction,
  AssetMission,
} from '../../types/user';
import { generateId } from './mockData';

const statuses: UserStatus[] = ['active', 'disabled', 'pending', 'suspended'];
const userTypes: UserType[] = ['asset', 'ghost'];

const formatDateTime = (date: Date): string => {
  const dateStr = date.toISOString().split('T')[0];
  const timeStr = date.toTimeString().slice(0, 5);
  return `${dateStr} ${timeStr}`;
};

const formatDate = (date: Date): string => {
  return date.toISOString().split('T')[0];
};

// Generate user ID like USR-10234
const generateUserId = (index: number): string => {
  const num = 10000 + index;
  return `USR-${num}`;
};

const generateMockCallSign = (userType: UserType, identifier: string): string => {
  const prefix = userType === 'asset' ? 'ASSET' : 'GHOST';
  const normalizedIdentifier = identifier.replace(/[^A-Z0-9]/gi, '').toUpperCase();

  return `${prefix}-${normalizedIdentifier}`;
};

// Predefined users matching the image
const predefinedUsers: User[] = [
  {
    id: generateId(),
    userId: 'USR-10234',
    name: 'ASSET-10234',
    email: 'asset-10234@example.com',
    status: 'active',
    userType: 'asset',
    lastLogin: new Date('2023-10-26T14:30:00'),
    lastLoginText: '2023-10-26 14:30',
    createdAt: new Date('2023-01-15'),
    createdAtText: '2023-01-15',
  },
  {
    id: generateId(),
    userId: 'USR-10235',
    name: 'GHOST-10235',
    email: 'ghost-10235@example.com',
    status: 'disabled',
    userType: 'ghost',
    lastLogin: new Date('2023-09-15T10:05:00'),
    lastLoginText: '2023-09-15 10:05',
    createdAt: new Date('2023-02-20'),
    createdAtText: '2023-02-20',
  },
  {
    id: generateId(),
    userId: 'USR-10236',
    name: 'ASSET-10236',
    email: 'asset-10236@example.com',
    status: 'pending',
    userType: 'asset',
    lastLogin: undefined,
    lastLoginText: 'Never',
    createdAt: new Date('2023-10-20'),
    createdAtText: '2023-10-20',
  },
  {
    id: generateId(),
    userId: 'USR-10237',
    name: 'GHOST-10237',
    email: 'ghost-10237@example.com',
    status: 'active',
    userType: 'ghost',
    lastLogin: new Date('2023-10-25T18:45:00'),
    lastLoginText: '2023-10-25 18:45',
    createdAt: new Date('2023-03-10'),
    createdAtText: '2023-03-10',
  },
];

// Generate additional mock users
const generateMockUser = (index: number): User => {
  const status = statuses[Math.floor(Math.random() * statuses.length)];
  const userType = userTypes[Math.floor(Math.random() * userTypes.length)];
  const userId = generateUserId(10000 + index);
  const name = generateMockCallSign(userType, userId);
  const email = `${name.toLowerCase()}@example.com`;

  const now = new Date();
  const daysAgo = Math.floor(Math.random() * 365);
  const createdAt = new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000);

  // Generate last login (some users never logged in)
  let lastLogin: Date | undefined;
  let lastLoginText: string;
  if (status === 'pending' || Math.random() < 0.1) {
    lastLogin = undefined;
    lastLoginText = 'Never';
  } else {
    const loginDaysAgo = Math.floor(Math.random() * daysAgo);
    lastLogin = new Date(now.getTime() - loginDaysAgo * 24 * 60 * 60 * 1000);
    lastLoginText = formatDateTime(lastLogin);
  }

  return {
    id: generateId(),
    userId,
    name,
    email,
    status,
    userType,
    lastLogin,
    lastLoginText,
    createdAt,
    createdAtText: formatDate(createdAt),
  };
};

// Generate 1000 users total (4 predefined + 996 generated)
export const mockUsers: User[] = [
  ...predefinedUsers,
  ...Array.from({ length: 996 }, (_, i) => generateMockUser(i + 4)),
];

// In-memory storage for user details to persist changes during the session
const sessionUserDetails: Record<string, UserDetail> = {};

// Generate user detail with all information
export const generateUserDetail = (id: string): UserDetail | null => {
  // Return from session storage if available
  if (sessionUserDetails[id]) {
    return sessionUserDetails[id];
  }

  const user = mockUsers.find(
    (u) => u.id === id || u.userId === id
  );

  if (!user) {
    return null;
  }

  // Generate tasks
  const tasks: UserTask[] = [
    {
      id: generateId(),
      taskId: '8A2B4C',
      title: 'Surveillance Op. Delta',
      status: 'completed',
      dateAssigned: new Date('2023-12-01'),
      dateAssignedText: 'Dec 1, 2023',
    },
    {
      id: generateId(),
      taskId: '9F3D5E',
      title: 'Locate Missing Asset',
      status: 'in_progress',
      dateAssigned: new Date('2024-01-05'),
      dateAssignedText: 'Jan 5, 2024',
    },
    {
      id: generateId(),
      taskId: '1G4H6J',
      title: 'Background Check Bravo',
      status: 'pending',
      dateAssigned: new Date('2024-02-10'),
      dateAssignedText: 'Feb 10, 2024',
    },
    {
      id: generateId(),
      taskId: 'K7L8M9',
      title: 'Data Analysis on Subject X',
      status: 'completed',
      dateAssigned: new Date('2023-11-22'),
      dateAssignedText: 'Nov 22, 2023',
    },
  ];

  // Generate payments
  const payments: UserPayment[] = [
    {
      id: generateId(),
      amount: 1250.00,
      date: new Date('2024-01-15'),
      dateText: 'Jan 15, 2024',
      status: 'completed',
      description: 'Task #8A2B4C',
    },
    {
      id: generateId(),
      amount: 850.00,
      date: new Date('2024-01-10'),
      dateText: 'Jan 10, 2024',
      status: 'completed',
      description: 'Task #K7L8M9',
    },
    {
      id: generateId(),
      amount: 1200.00,
      date: new Date('2024-01-05'),
      dateText: 'Jan 5, 2024',
      status: 'pending',
      description: 'Task #9F3D5E',
    },
  ];

  // Generate warnings
  const warnings: UserWarning[] = [
    {
      id: generateId(),
      issuedBy: 'admin-001',
      issuedByName: 'John Doe',
      reason: 'Late submission of case file #78901',
      date: new Date('2023-09-20'),
    },
    {
      id: generateId(),
      issuedBy: 'admin-002',
      issuedByName: 'Jane Smith',
      reason: 'Client complaint regarding communication',
      date: new Date('2023-06-15'),
    },
  ];

  // Generate activities
  const activities: UserActivity[] = [
    {
      id: generateId(),
      type: 'login',
      description: 'User logged in',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
      timestampText: '2 hours ago',
    },
    {
      id: generateId(),
      type: 'task_completed',
      description: 'Completed task #8A2B4C',
      timestamp: new Date('2023-12-05'),
      timestampText: 'Dec 5, 2023',
    },
    {
      id: generateId(),
      type: 'profile_updated',
      description: 'Updated profile information',
      timestamp: new Date('2023-11-10'),
      timestampText: 'Nov 10, 2023',
    },
  ];

  let userDetail: UserDetail;

  // For Ghost users, add additional information
  if (user.userType === 'ghost') {
    userDetail = {
      ...user,
      phone: '+1 (555) 123-4567',
      personalInfo: {
        email: user.email,
        phone: '+1 (555) 123-4567',
        dateOfBirth: 'July 15, 1988',
        address: '123 Main Street, Anytown, USA 12345',
      },
      license: {
        licenseNumber: 'PI-123456789',
        yearsOfExperience: 8,
        licensedStates: ['California', 'Nevada', 'Arizona'],
      },
      skills: ['Surveillance', 'Digital Forensics', 'Interviews', 'Background Checks', 'Report Writing'],
      performanceMetrics: {
        completionRate: 92,
        averageRating: 4.8,
        casesCompleted: 114,
      },
      earnings: {
        totalEarnings: 76450.00,
        totalPayout: 70630.00,
      },
      warnings,
      tasks,
      payments,
      activities,
      isVerified: user.status === 'active',
    };
  } else {
    // For Asset users - add Asset-specific data
    const totalMissions = Math.floor(Math.random() * 50) + 5; // 5-54 missions
    const completedMissions = Math.floor(totalMissions * (0.6 + Math.random() * 0.3));

    const paymentMethods: PaymentMethod[] = [
      {
        id: generateId(),
        type: 'credit_card',
        last4: '4242',
        brand: 'visa',
        expiryMonth: 12,
        expiryYear: 2025,
        isDefault: true,
        holderName: user.name,
      },
    ];

    const preferredGhosts: string[] = ['GHOST-1001', 'GHOST-1005', 'GHOST-1012'];
    const blacklistedGhosts: string[] = ['GHOST-2003'];

    const spyDollarBalance: SpyDollarBalance = {
      currentBalance: Math.floor(Math.random() * 5000) + 500,
      pendingBalance: Math.floor(Math.random() * 500),
      totalEarned: Math.floor(Math.random() * 10000) + 2000,
      totalSpent: Math.floor(Math.random() * 8000) + 1000,
    };

    const spyDollarTransactions: SpyDollarTransaction[] = [
      {
        id: generateId(),
        type: 'earned',
        amount: 500,
        description: 'Mission completion bonus',
        missionId: 'MSN-001',
        missionTitle: 'Surveillance Operation Alpha',
        date: new Date('2024-01-15'),
        dateText: 'Jan 15, 2024',
        status: 'completed',
      },
    ];

    const assetMissions: AssetMission[] = [
      {
        id: generateId(),
        code: 'MSN-001',
        title: 'Surveillance Operation Alpha',
        status: 'completed',
        createdAt: '2024-01-10T10:00:00Z',
        completedAt: '2024-01-15T14:30:00Z',
        amount: 1250.00,
        ghostName: 'John Smith',
      },
    ];

    userDetail = {
      ...user,
      phone: '+1 (555) 123-4567',
      personalInfo: {
        email: user.email,
        phone: '+1 (555) 123-4567',
        address: '123 Main Street, Anytown, USA 12345',
      },
      tasks,
      payments,
      warnings,
      activities,
      isVerified: user.status === 'active',
      // Asset-specific fields
      paymentMethods,
      preferredGhosts,
      blacklistedGhosts,
      spyDollarBalance,
      spyDollarTransactions,
      assetMissions,
      // Mission statistics
      totalMissions,
      completedMissions,
    };
  }

  // Store in session storage
  sessionUserDetails[id] = userDetail;
  sessionUserDetails[user.id] = userDetail;
  if (user.userId) {
    sessionUserDetails[user.userId] = userDetail;
  }

  return userDetail;
};

// Utility to add a warning to a mock user
export const addWarningToMockUser = (userId: string, reason: string): void => {
  const detail = generateUserDetail(userId);
  if (detail) {
    const newWarning: UserWarning = {
      id: generateId(),
      issuedBy: 'admin-001',
      issuedByName: 'Administrator',
      reason: reason,
      date: new Date(),
    };

    detail.warnings = [newWarning, ...(detail.warnings || [])];

    // Update session storage
    sessionUserDetails[userId] = detail;
    if (detail.id) sessionUserDetails[detail.id] = detail;
    if (detail.userId) sessionUserDetails[detail.userId] = detail;
  }
};
